import { Injectable, OnDestroy } from '@angular/core';
import { Client, IFrame, IMessage, StompSubscription } from '@stomp/stompjs';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Notification } from '../../common/models/notification/notification';
import { AuthenticationService } from '../authentication.service';
import { ProfileService } from '../profile.service';
import SockJS from 'sockjs-client';

const RECONNECT_DELAY_MS = 5000;
const NOTIFICATION_QUEUE = '/user/queue/notifications';

@Injectable({
  providedIn: 'root',
})
export class NotificationWebsocketService implements OnDestroy {
  public notifications$ = new Subject<Notification>();

  private _client: Client | null = null;
  private _subscription: StompSubscription | null = null;
  private _connected = false;

  constructor(
    private _authenticationService: AuthenticationService,
    private _profileService: ProfileService
  ) {}

  public connect(): void {
    if (this._connected) {
      return;
    }

    const token = this._authenticationService.getToken();
    const wsUrl = this._buildWsUrl();

    this._client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: RECONNECT_DELAY_MS,
      onConnect: (frame: IFrame) => this._onConnect(frame),
      onDisconnect: () => this._onDisconnect(),
      onStompError: (frame: IFrame) => this._onStompError(frame),
    });

    this._client.activate();
  }

  public disconnect(): void {
    this._subscription?.unsubscribe();
    this._subscription = null;

    if (this._client) {
      this._client.deactivate();
      this._client = null;
    }

    this._connected = false;
  }

  public ngOnDestroy(): void {
    this.disconnect();
    this.notifications$.complete();
  }

  private _onConnect(_frame: IFrame): void {
    this._connected = true;

    const profileId = this._profileService.profileSubject.getValue()?.id;
    const destination = profileId
      ? `/user/${profileId}/queue/notifications`
      : NOTIFICATION_QUEUE;

    this._subscription = this._client!.subscribe(
      destination,
      (message: IMessage) => this._handleMessage(message)
    );
  }

  private _onDisconnect(): void {
    this._connected = false;
    this._subscription = null;
  }

  private _onStompError(frame: IFrame): void {
    console.error(
      '[NotificationWebsocket] STOMP error',
      frame.headers['message']
    );
  }

  private _handleMessage(message: IMessage): void {
    try {
      const notification: Notification = JSON.parse(message.body);
      this.notifications$.next(notification);
    } catch {
      console.error(
        '[NotificationWebsocket] Failed to parse message',
        message.body
      );
    }
  }

  private _buildWsUrl(): string {
    return environment.notificationsWebsocket;
  }
}
